'use client';

import { useIntl } from 'react-intl';
import Image from '@alltrails/core/components/Image';
import InfoDividerDot from '@alltrails/core/components/InfoDividerDot';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import { getFormattedDistance } from '@alltrails/data-formatting/distanceFormatters';
import { ratingToFormattedRating } from '@alltrails/data-formatting/numberFormatters';
import PhotoSlash from '@alltrails/denali/icons/PhotoSlash';
import StarFilled from '@alltrails/denali/icons/StarFilled';
import { getTrailDifficultyLabelFromNumber } from '@alltrails/difficulty/utils/trailDifficultyHelpers';
import { AlgoliaTrail } from '@alltrails/trails';
import { useAppSelector } from '@/utils/redux';
import styles from './FeaturedTrailsModule.module.scss';

type FeaturedTrailsModuleCardProps = {
  trail: AlgoliaTrail;
  label?: string;
};

const FeaturedTrailsModuleCard = ({ trail, label }: FeaturedTrailsModuleCardProps) => {
  const intl = useIntl();
  const languageRegionCode = useLanguageRegionCode();
  const displayMetric = useAppSelector(state => state.context.displayMetric);

  const localizedRating = ratingToFormattedRating(trail.avg_rating || 0, intl);
  const difficultyText = getTrailDifficultyLabelFromNumber(intl, parseInt(trail.difficulty_rating || '0'));

  return (
    <a href={wrapUrlSafe(`/${trail.slug}`, languageRegionCode)} className={styles.card}>
      {trail.profile_photo_url ? (
        <Image className={styles.trailImage} src={trail.profile_photo_url} alt={trail.name} width={75} height={75} loading="lazy" />
      ) : (
        <div className={styles.emptyImage}>
          <PhotoSlash color="--color-text-secondary" />
        </div>
      )}
      <div className={styles.trailInfo}>
        {label && <div className={styles.typeLabel}>{label}</div>}
        <div className={styles.trailName}>{trail.name}</div>
        <div className={styles.trailDetails}>
          {!!trail.avg_rating && (
            <>
              <StarFilled color="--color-text-secondary" className={styles.star} />
              <span>{localizedRating}</span>
            </>
          )}
          {!!trail.difficulty_rating && (
            <div>
              {(!!trail.avg_rating || !!trail.num_reviews) && <InfoDividerDot />}
              <span>{difficultyText}</span>
            </div>
          )}
          {!!trail.length && (
            <div className={styles.trailLength}>
              {(!!trail.difficulty_rating || !!trail.avg_rating) && <InfoDividerDot />}
              <span>{getFormattedDistance(intl, trail.length, displayMetric)}</span>
            </div>
          )}
        </div>
      </div>
    </a>
  );
};

export default FeaturedTrailsModuleCard;
